import * as React from "react";
import { useState, useRef, useEffect, forwardRef, ChangeEvent, useImperativeHandle } from "react";
import axios from "axios";
import { useDebounce } from "use-debounce";

import "./ginput.scss";
import { ItemMatcher } from "./ItemMatcher";
import { GInputSearchHits } from "./GInputSearchHits";
import { GInputErrorMessage } from "./GInputErrorMessage";

const getUID = () =>
    (Math.floor(Math.random() * 1e+18)).toString(16)
        .match(/.{1,4}/g)
        .join("-");

export interface IValidatorFunction {
    (value: string): string | null;
}

interface GInputProps<T> {
    label: string;
    placeholder?: string;
    remote?: {
        url: string;
        paramName: string;
    };
    paramName?: string;
    items?: unknown[];
    itemPropNames?: string[];

    onChange<T>(value: T);

    mandatory?: boolean;
    mandatoryText?: string;
    ref?: any;
    validators?: IValidatorFunction[];

    innerClass?: string;
    outerClass?: string;

    blankOptionLabel?: string;
    initialValue?: string;
}

export interface GInputHandles {
    clear(): void;

    isValid(): boolean;
}


const defaultProps = {
    label: "",
    placeholder: "",
    innerClass: "",
    outerClass: "",
    remote: null,
    items: null,
    itemPropNames: null,
    onChange: null,
    mandatory: false,
    mandatoryText: "Mandatory field",
    validators: [],
    blankOptionLabel: "",
    initialValue: ""
};

export const GInput: <T = string>(x: any) => any = forwardRef(
    function of<T = string>(props: GInputProps<T>, ref) {
        const {
            label, placeholder = "",
            innerClass = "", outerClass = "",
            remote, items = null, itemPropNames = null,
            onChange,
            mandatory, mandatoryText,
            validators = [],
            initialValue
        } = { ...defaultProps, ...props };
        const uid = useRef(getUID());

        const [ searchValue, setSearchValue ] = useState(initialValue);
        const [ searchHits, setSearchHits ] = useState([]);
        const [ skipSearch, setSkipSearch ] = useState(false);
        const [ error, setError ] = useState(null);
        const [ wasBlurred, setWasBlurred ] = useState(false);
        const [ selectedItem, setSelectedItem ] = useState(null);
        const [ showError, setShowError ] = useState(false);
        const [ debouncedSearchValue ] = useDebounce(searchValue, 700);
        const [ selectedSearchHitIdx, setSelectedSearchHitIdx ] = useState(null);

        const skipBlur = useRef(false);
        const itemMatcher = useRef(ItemMatcher.create({
            searchProps: itemPropNames ? itemPropNames : null
        }));

        const [ usesItemSelection, setUsesItemSelection ] = useState(!!(items || remote));

        useEffect(() => {
            setUsesItemSelection(!!(items || remote));
        }, [
            items, remote
        ]);

        const getErrorMessage = (search, selectedItem, mandatory, validators) => {
            const trimmedSearchValue = search.trim();
            let errorMessage = "";
            if (usesItemSelection) {
                if (mandatory) {
                    const missingData = mandatory && !selectedItem;
                    if (missingData) {
                        errorMessage = mandatoryText;
                    }
                }
            }
            else {
                let missingData = false;
                if (mandatory) {
                    missingData = 0 == trimmedSearchValue.length;
                    if (missingData) {
                        errorMessage = mandatoryText;
                    }
                }
                if (!missingData) {
                    // also, run any validators
                    errorMessage = validators.reduce((err, validator) => {
                        if (null != err) { return err; }
                        return validator(trimmedSearchValue);
                    }, null) as string;
                }
            }
            return errorMessage;
        };


        useEffect(() => {
            let errorMessage = getErrorMessage(searchValue, selectedItem, mandatory, validators);
            setShowError(wasBlurred && !!errorMessage);
            setError(errorMessage);
        }, [ wasBlurred, selectedItem, searchValue ]);

        useImperativeHandle(ref, () => ({
            clear() {
                setSearchValue("");
                if (usesItemSelection) {
                    setSelectedItem(null);
                }
                setError(null);
                setShowError(false);
                setWasBlurred(false);
            },
            isValid() {
                return !getErrorMessage(searchValue, selectedItem, mandatory, validators); // make real validation
            }
        }));


        const handleSearchFieldChange = (value: string) => {
            setSearchValue(value);
            if (usesItemSelection) {
                setSelectedItem(null);
                onChange && onChange(null);
            }
            else {
                // @ts-ignore
                onChange && onChange(value);
            }
        };

        useEffect(() => {
            if (!usesItemSelection) { return; }
            if (skipSearch) {
                setSkipSearch(false);
                return;
            }

            let search;
            if (remote) {
                search = async (q: string) => {
                    const { url, paramName } = remote;
                    // setIsSearching(true);
                    const response = await axios.get(`${ url }?${ paramName }=${ encodeURIComponent(q) }`);
                    // setIsSearching(false);
                    const hits = response.data;
                    setSearchHits(hits);
                };
            }
            else {
                if (items) {
                    search = async (q: string) => {
                        const hits = await itemMatcher.current.setItems(items).getMatches(q);
                        setSearchHits(hits);
                    };
                }
            }
            if (search) {
                if (debouncedSearchValue) {
                    search(debouncedSearchValue);
                    setSelectedSearchHitIdx(0);
                }
                else {
                    setSearchHits([]);
                }
            }

        }, [ debouncedSearchValue ]);

        const closeSearchHits = () => {
            setSearchHits([]);
        };

        const selectItem = item => {
            setSelectedItem(item);
            closeSearchHits();
            setSkipSearch(true);
            const value = null == item ? "" : itemPropNames ? item[itemPropNames[0]] : item;
            setSearchValue(value);
            onChange && onChange(item);
        };


        const searchFieldKeyHandler = (e) => {
            const { key: keyPressed } = e;
            switch (keyPressed) {
                case "Enter":
                    const item = searchHits[selectedSearchHitIdx];
                    selectItem(item);
                    e.preventDefault();
                    break;
                case "Escape":
                    selectItem(null);
                    break;
                case "ArrowUp":
                    setSelectedSearchHitIdx(Math.max(0, selectedSearchHitIdx - 1));
                    break;
                case "ArrowDown":
                    setSelectedSearchHitIdx(Math.min(searchHits.length - 1, selectedSearchHitIdx + 1));
                    break;
                default:
                    break;
            }
        };

        const searchFieldBlurHandler = () => {
            if (skipBlur.current) {
                skipBlur.current = false;
                return;
            }
            if (items && !selectedItem) {
                if (1 == searchHits.length) { selectItem(searchHits[0]); }
                else { setSearchValue(""); }
            }
            closeSearchHits();
            setWasBlurred(true);
        };

        const htmlId = `g-input__${ uid.current }`;
        return (
            <div className={ `g-input-wrap ${ showError ? "g-input-wrap--error" : "" } ${ outerClass }` }>
                <label htmlFor={ htmlId }>{ label }</label>
                <input className={ `g-input-inner ${ innerClass }` } id={ htmlId }
                    placeholder={ placeholder }
                    value={ searchValue }
                    onChange={ (e: ChangeEvent<HTMLInputElement>) => handleSearchFieldChange(e.target.value) }
                    onKeyDown={ searchFieldKeyHandler }
                    onBlur={ searchFieldBlurHandler }
                />
                <GInputErrorMessage label={ showError ? error : null }/>
                <GInputSearchHits
                    hits={ searchHits }
                    itemPropNames={ itemPropNames }
                    selectedSearchHitIdx={ selectedSearchHitIdx }
                    select={ (item: T) => {
                        skipBlur.current = true;
                        selectItem(item);
                    } }
                />

            </div>
        );
    });

//GInput.displayName = "";
